import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { supportMailboxFilters, type SupportMailboxFilter } from '@brimo/shared';
import {
  Banner,
  Button,
  EmptyState,
  FilterChip,
  SearchField,
  Skeleton,
  useToast,
} from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import { errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { LockedPage } from '../../../shell/ModulePages';
import {
  assignMail,
  ignoreMail,
  notifyMailboxAdmins,
  reconnectMailbox,
  ticketFromMail,
  useInvalidateTickets,
  useSupportMailbox,
  type MailAssignTarget,
} from '../api';
import type { MailboxRowData, MailboxSuggestion } from '../types';
import { LoadError, isForbidden } from '../ui/LoadError';
import { TicketsHeader } from '../ui/TicketsHeader';
import page from '../ui/page.module.css';
import { ContactSearchDialog } from './ContactSearchDialog';
import { MailboxRow } from './MailboxRow';
import styles from './mailbox.module.css';

function isMailboxFilter(value: string | null): value is SupportMailboxFilter {
  return (supportMailboxFilters as readonly string[]).includes(value ?? '');
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function targetOf(suggestion: MailboxSuggestion): MailAssignTarget | null {
  if (suggestion.kind === 'contact' && suggestion.contactId) return { contactId: suggestion.contactId };
  if (suggestion.kind === 'deal' && suggestion.dealId) return { dealId: suggestion.dealId };
  if (suggestion.kind === 'after_sales' && suggestion.caseId) return { caseId: suggestion.caseId };
  return null;
}

/**
 * `/tickets/mailbox` — the organization's support mailbox (After-Sales 01; 02 while the connection is lost). Filter
 * pills `?filter=unassigned|today|all`, search `?q=` over sender and subject; rows are assigned to the suggested
 * contact / deal / case, searched by contact, or turned into a ticket.
 */
export function MailboxTab() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { toast } = useToast();
  const invalidate = useInvalidateTickets();
  const [params, setParams] = useSearchParams();
  const filterParam = params.get('filter');
  const filter: SupportMailboxFilter = isMailboxFilter(filterParam) ? filterParam : 'unassigned';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const debounced = useDebounced(search.trim(), 300);
  const list = useSupportMailbox(filter, q);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [searching, setSearching] = useState<MailboxRowData | null>(null);
  const [connectionPending, setConnectionPending] = useState<'reconnect' | 'notify' | null>(null);

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    if (debounced !== q) patch({ q: debounced || null });
  }, [debounced]);

  if (permissions.tool('tickets') !== 'ok' || isForbidden(list.error)) return <LockedPage tool="tickets" />;

  async function onRow(row: MailboxRowData, action: () => Promise<unknown>) {
    setBusyId(row.id);
    try {
      await action();
      await invalidate();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusyId(null);
    }
  }

  function assign(row: MailboxRowData) {
    const target = row.suggestion ? targetOf(row.suggestion) : null;
    if (!target) return;
    void onRow(row, async () => {
      await assignMail(row.id, target);
      toast({ message: `Zugeordnet: ${row.suggestion?.label ?? row.subject}` });
    });
  }

  function ticket(row: MailboxRowData) {
    void onRow(row, async () => {
      const created = await ticketFromMail(row.id);
      toast({
        message: `Ticket ${created.numberLabel} angelegt.`,
        action: { label: 'Öffnen', onClick: () => void navigate(`/tickets/${created.ticketId}`) },
        countdown: false,
      });
    });
  }

  async function reconnect(connectionId: string) {
    setConnectionPending('reconnect');
    try {
      const result = await reconnectMailbox(connectionId);
      window.location.assign(result.url);
    } catch (error) {
      toast({ message: errorMessage(error) });
      setConnectionPending(null);
    }
  }

  async function notifyAdmins() {
    setConnectionPending('notify');
    try {
      await notifyMailboxAdmins();
      toast({ message: 'Die Admins sind informiert.' });
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setConnectionPending(null);
    }
  }

  const first = list.data?.pages[0];
  const rows = list.data?.pages.flatMap((entry) => entry.rows) ?? [];
  const state = first?.mailbox.state ?? null;
  const settingsHref = first?.settingsHref ?? '/settings/connections';

  return (
    <div className={clsx(page.page, page.tight)}>
      <TicketsHeader
        eyebrow="Tickets"
        title="Postfach"
        line={state === 'disconnected' ? first?.mailbox.address : first?.line}
        tab="mailbox"
        actions={
          state === 'connected' ? (
            <>
              <SearchField
                label="Postfach durchsuchen"
                placeholder="Absender, Betreff"
                icon={false}
                className={styles.search}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Button variant="secondary" onClick={() => void navigate(settingsHref)}>
                Postfach-Einstellungen
              </Button>
            </>
          ) : null
        }
      />

      {list.isError ? (
        <LoadError
          what="Das Postfach konnte nicht geladen werden"
          error={list.error}
          retrying={list.isFetching}
          onRetry={() => void list.refetch()}
        />
      ) : null}

      {list.isPending ? (
        <div className={page.skeleton} aria-busy="true">
          <Skeleton width={420} height={30} />
          <Skeleton variant="block" height={360} />
        </div>
      ) : null}

      {first && state === 'none' ? (
        <div className={page.emptyWrap}>
          <EmptyState
            eyebrow="Postfach"
            title="Kein Support-Postfach verbunden."
            description="Verbinden Sie das Support-Postfach der Organisation. Neue Anfragen landen dann hier und lassen sich einem Kontakt zuordnen oder als Ticket anlegen."
            actions={<Button onClick={() => void navigate(settingsHref)}>Postfach verbinden</Button>}
          />
        </div>
      ) : null}

      {first && state === 'disconnected' ? (
        <>
          {first.banner ? (
            <Banner tone="danger" title={first.banner.title}>
              {first.banner.text}
            </Banner>
          ) : null}
          <div className={styles.connectionActions}>
            {first.mailbox.connectionId ? (
              <Button
                loading={connectionPending === 'reconnect'}
                disabled={connectionPending === 'notify'}
                onClick={() => {
                  const connectionId = first.mailbox.connectionId;
                  if (connectionId) void reconnect(connectionId);
                }}
              >
                Neu verbinden
              </Button>
            ) : null}
            <Button
              variant="secondary"
              loading={connectionPending === 'notify'}
              disabled={connectionPending === 'reconnect'}
              onClick={() => void notifyAdmins()}
            >
              Admin informieren
            </Button>
          </div>
          {rows.length > 0 ? (
            <div className={styles.inert} inert aria-hidden="true">
              <ul className={styles.list}>
                {rows.map((row) => (
                  <MailboxRow
                    key={row.id}
                    row={row}
                    busy={false}
                    inert
                    onAssign={assign}
                    onTicket={ticket}
                    onSearch={setSearching}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}

      {first && state === 'connected' ? (
        <>
          <div className={styles.pills} role="group" aria-label="Filter">
            {first.pills.map((pill) => (
              <FilterChip
                key={pill.key}
                label={pill.label}
                count={pill.count.toLocaleString('de-DE')}
                active={pill.active}
                onClick={() => patch({ filter: pill.key === 'unassigned' ? null : pill.key })}
              />
            ))}
          </div>
          {rows.length === 0 ? (
            <div className={styles.emptyList}>
              {q
                ? 'Keine E-Mail passt zur Suche.'
                : filter === 'unassigned'
                  ? 'Alle E-Mails sind zugeordnet.'
                  : 'Keine E-Mails in dieser Ansicht.'}
            </div>
          ) : (
            <ul
              className={clsx(styles.list, list.isPlaceholderData && list.isFetching && styles.dimmed)}
              aria-label="E-Mails"
            >
              {rows.map((row) => (
                <MailboxRow
                  key={row.id}
                  row={row}
                  busy={busyId === row.id}
                  onAssign={assign}
                  onTicket={ticket}
                  onSearch={setSearching}
                />
              ))}
            </ul>
          )}
          {list.hasNextPage ? (
            <div className={styles.more}>
              <Button
                variant="secondary"
                loading={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                Weitere laden
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {searching ? (
        <ContactSearchDialog
          row={searching}
          onAssign={async (contactId) => {
            await assignMail(searching.id, { contactId });
            await invalidate();
            toast({ message: 'Die E-Mail ist dem Kontakt zugeordnet.' });
          }}
          onIgnore={async () => {
            await ignoreMail(searching.id);
            await invalidate();
          }}
          onClose={() => setSearching(null)}
        />
      ) : null}
    </div>
  );
}
